import { alpha, styled } from '@mui/material/styles';

/**
 * Signature styles — converted from `Signature.module.scss` when the component moved into
 * `shared/ui`, which was otherwise SCSS-free.
 *
 * WHY THE COLOURS MOVED SLIGHTLY. The SCSS reached into the legacy `global-ui-scss` island for
 * `$primary-dark: #003e47` and a `RgbaColor()` helper. That hex is a FROZEN COPY of the pre-Figma
 * palette: `styles/tokens/primitives.ts` records `brand[128]: figma.brand[175] // was #003E47 →
 * #003239`. So the component had been rendering the old brand dark ever since the palette
 * migration, while the rest of the app moved on. Reading `theme.palette.primary.darker` here is
 * what stops that drifting again — the shift is ~5/255 per channel and not perceptible, but it is
 * a real change and not a pure refactor.
 *
 * Everything else is value-identical to the SCSS: `theme.spacing(1)` IS `0.5rem` on the 8px base,
 * and `grey[90]` (#D6E0E2, the token the theme reserves for input outlines) replaces the
 * hand-written #dae3e5.
 *
 * Font sizes stay in `rem` rather than becoming px: they were rem in the SCSS, and rem keeps
 * honouring the reader's browser font-size setting.
 */

/** Drawing-surface size. Shared with the `<canvas>` width/height ATTRIBUTES in `Signature.tsx` —
 *  those set the bitmap resolution, these set the CSS box, and the two must agree or strokes
 *  land offset from the cursor. The SCSS duplicated both numbers; this is the single source. */
export const CANVAS_WIDTH = 240;
export const CANVAS_HEIGHT = 146;

/**
 * The "qualified electronic signature" band. Six bespoke stops: three are brand/100 (#49BACA) but
 * #245C64, #488993 and #7CE1EF have no palette equivalent at any step of the ramp, so this is one
 * indivisible brand artwork rather than an application of tokens. Tokenising the three that match
 * would imply the other three were tokens too. Kept verbatim from the SCSS, on purpose.
 */
const SIGNATURE_BAND_GRADIENT =
  'linear-gradient(90.38deg, #49baca 0.33%, #245c64 20.61%, #49baca 40.89%, #488993 61.17%, #7ce1ef 94.63%, #49baca 101.73%)';

export const Wrapper = styled('div')(({ theme }) => ({
  border: `1px solid ${theme.palette.grey[90]}`,
  borderRadius: 4,
}));

export const TopContent = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '10fr 12fr',
  gap: theme.spacing(2),
  padding: theme.spacing(1),
}));

export const TopLeft = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  paddingBlock: theme.spacing(1),
}));

/** Positioning context for the absolutely-placed uploaded-signature overlay. */
export const TopRight = styled('div')({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  // `react-signature-canvas` renders a bare <canvas> and only forwards `canvasProps`, so it cannot
  // take a styled wrapper. Reaching it by element selector is what lets the CSS box live here with
  // the rest of the layout instead of as a className threaded through props.
  '& canvas': {
    minWidth: 0,
    width: CANVAS_WIDTH,
    height: CANVAS_HEIGHT,
  },
});

export const Title = styled('h4')({
  fontSize: '1.125rem',
  fontWeight: 700,
  marginBottom: '0.25rem',
});

export const CompanyName = styled('div')(({ theme }) => ({
  fontWeight: 700,
  fontSize: '0.875rem',
  color: alpha(theme.palette.primary.darker, 0.8),
}));

export const SignedAt = styled('div')(({ theme }) => ({
  color: alpha(theme.palette.primary.darker, 0.6),
}));

/**
 * The previously-uploaded signature, laid over the live pad. `pointer-events: none` is load-bearing:
 * without it the overlay would swallow the strokes meant for the canvas underneath.
 */
export const UploadedSignature = styled('img')({
  minWidth: 0,
  position: 'absolute',
  objectFit: 'contain',
  userSelect: 'none',
  height: 'auto',
  width: 'auto',
  maxWidth: '100%',
  maxHeight: '100%',
  pointerEvents: 'none',
});

export const SignatureBand = styled('div')(({ theme }) => ({
  color: theme.palette.common.white,
  fontSize: '0.75rem',
  fontWeight: 700,
  padding: theme.spacing(1, 2),
  background: SIGNATURE_BAND_GRADIENT,
}));
